import type React from "react";
import type { AppBannerVariant } from "@/interfaces/config/SearchBannerSettings";

type AppBannerProps = {
	message: string;
	variant?: AppBannerVariant;
	dismissible?: boolean;
	onDismiss?: () => void;
	className?: string;
};

const BANNER_STYLES: Record<
	AppBannerVariant,
	{ container: string; icon: string; role: "status" | "alert" }
> = {
	info: {
		container: "border-blue-200 bg-blue-50 text-blue-900",
		icon: "icon-info",
		role: "status",
	},
	warning: {
		container: "border-yellow-500 bg-yellow-100 text-yellow-900",
		icon: "icon-warning",
		role: "alert",
	},
	success: {
		container: "border-green-200 bg-green-50 text-green-900",
		icon: "icon-check",
		role: "status",
	},
	error: {
		container: "border-red-200 bg-red-50 text-red-900",
		icon: "icon-error",
		role: "alert",
	},
};

const AppBanner: React.FC<AppBannerProps> = ({
	message,
	variant = "info",
	dismissible = false,
	onDismiss,
	className = "",
}) => {
	const styles = BANNER_STYLES[variant];

	return (
		<div
			className={`flex items-start justify-between gap-4 rounded-md border px-6 py-4 text-base shadow-sm ${styles.container} ${className}`.trim()}
			role={styles.role}
		>
			<span className={`${styles.icon} mt-0.5 shrink-0 text-lg`} aria-hidden="true" />
			<p className="min-w-0 flex-1 text-base leading-6">{message}</p>
			{dismissible && onDismiss && (
				<button
					type="button"
					aria-label="Close notification"
					className="flex h-6 w-6 shrink-0 items-center justify-center rounded p-0 text-current transition-opacity hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-current"
					onClick={onDismiss}
				>
					<span className="icon-close text-lg leading-none" aria-hidden="true" />
				</button>
			)}
		</div>
	);
};

export default AppBanner;
